import { Icon } from '@/app/components/Icon'
import { cn } from '@/app/utils/style'
import { LocalIconName } from '@/icons'
import { StructuredDataQueryResult } from '@/sanity.types'

type SocialProfiles = NonNullable<
  NonNullable<StructuredDataQueryResult>['organizationShared']
>['socialProfiles']

interface SocialProps {
  profiles: SocialProfiles | null | undefined
  classNames?: {
    root?: string
    link?: string
  }
  iconSize?: number
  order?: LocalIconName[]
}

const platformLabels: Record<LocalIconName, string> = {
  facebook: 'Facebook',
  twitter: 'Twitter / X',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  github: 'GitHub',
  pinterest: 'Pinterest',
}

export const applySocialOrder = <T extends { platform: LocalIconName }>(
  items: T[],
  order: LocalIconName[],
): T[] => {
  return items.sort((a, b) => {
    const indexA = order.indexOf(a.platform)
    const indexB = order.indexOf(b.platform)

    // If both are in the order array, sort by their position in the order
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB
    }

    // If only a is in the order, it comes first
    if (indexA !== -1) return -1

    // If only b is in the order, it comes first
    if (indexB !== -1) return 1

    // If neither is in the order, maintain their original order
    return 0
  })
}

export const Social = ({ profiles, classNames, iconSize = 24, order }: SocialProps) => {
  if (!profiles) return null

  let socialLinks = (Object.keys(profiles) as LocalIconName[])
    .filter((platform) => profiles[platform])
    .map((platform) => ({
      platform,
      url: profiles[platform]!,
      label: platformLabels[platform],
    }))

  // Apply custom ordering if provided
  if (order && order.length > 0) {
    socialLinks = applySocialOrder(socialLinks, order)
  }

  if (socialLinks.length === 0) return null

  return (
    <div className={cn('flex items-center gap-lg', classNames?.root)}>
      {socialLinks.map(({ platform, url, label }) => (
        <a
          key={platform}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          className={cn('transition-colors duration-200', classNames?.link)}
        >
          <Icon name={platform} size={iconSize} />
        </a>
      ))}
    </div>
  )
}
